import type { FocusEvent, FC } from 'react'
import { useRef } from 'react'
import { FieldInputProps, useField } from 'formik'
import { TextField, TextFieldProps } from '@mui/material'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'

import { ErrorInputAdornment } from './ErrorInputAdornment.js'

export type NumberFieldInputProps = Omit<
  TextFieldProps,
  keyof FieldInputProps<number> | 'inputProps' | 'inputRef' | 'variant' | 'type' | 'error'
> & {
  name: string
  min?: number
  max?: number
  step?: number
  helperText?: string
  selectAllOnFocus?: boolean
  onNativeFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

export const NumberFieldInput: FC<NumberFieldInputProps> = ({
  name,
  min,
  max,
  step,
  helperText,
  tabIndex,
  selectAllOnFocus = false,
  onNativeFocus,
  slotProps: propsSlotProps,
  ...props
}: NumberFieldInputProps) => {
  const [field, meta] = useField<number>(name)
  const inputRef = useRef<HTMLInputElement>(null)

  useFocusOnFirstError(name, inputRef, inputRef)

  return (
    <TextField
      {...props}
      {...field}
      inputRef={inputRef}
      value={field.value}
      onFocus={(event) => {
        if (selectAllOnFocus) {
          event.target.select()
        } else if (onNativeFocus) {
          onNativeFocus(event)
        }
      }}
      variant="outlined"
      type="number"
      error={!!meta.error && meta.touched}
      helperText={meta.touched && meta.error ? meta.error : helperText}
      slotProps={{
        input: {
          endAdornment: <ErrorInputAdornment visible={meta.touched && !!meta.error} />,
          ...propsSlotProps?.input,
        },
        formHelperText: {
          ...propsSlotProps?.formHelperText,
        },
        htmlInput: { min, step, max, tabIndex, ...propsSlotProps?.htmlInput },
      }}
    />
  )
}
